import {connect} from "react-redux";
import { NetworkData } from "../../fstn/network";
import {selectNetwork, selectSubtitles} from "../../redux/selectors/firebase";
import {Box, TextField, Typography} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {LocalizedLanguages} from "./Localization";
import {SubtitleRow} from "./SubtitleRow";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {UpdateSubtitleDialog} from "./UpdateSubtitleDialog";
import {FixedSizeList} from "react-window";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {exportSubtitles} from "../../utility/cookbookExporter";
import {saveAs} from "file-saver";
import {ILocalizationData, updateModeData, UpdateModeData} from "../../redux/slices/editor";
import {selectModeData} from "../../redux/selectors/editor";
import { storeKeyName } from "./subtitleUtils";

const useStyles = makeStyles((theme) => ({
    content: {
        height: "calc(100% - 112px)",
        width: "100%"
    },
    date: {
        marginTop: theme.spacing(8),
        margin: theme.spacing(1),
        width: 90
    },
    blockName: {
        display: "flex",
        flexGrow: 1,
        marginTop: theme.spacing(8),
        margin: theme.spacing(1),
        width: 90
    },
    language: {
        paddingTop: theme.spacing(3),
        marginTop: theme.spacing(8),
        transform: "rotate(315deg)",
        whiteSpace: "nowrap",
        width: 30
    },
    languageLabel: {
        borderBottom: "2px solid lightgrey"
    },
    table: {
        marginRight: theme.spacing(10)
    },
    tableContent: {
        maxHeight: "calc(100% - 112px)",
        overflowY: "scroll",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    tableHead: {
    },
}));

export type Subtitles = {[block: string]: ISubtitles}

export interface ILanguageSubtitles {
    cues: ICue []
    modified: number
}
export interface ISubtitles extends ILanguageSubtitles {
    languages: {[language: string]: ILanguageSubtitles}
}
export interface ICue {
    start: number
    stop: number
    text: string
}

interface ILocalizedSubtitles {
    localizationData: ILocalizationData
    network: NetworkData
    project: string
    subtitles: Subtitles
    updateModeData: UpdateModeData
}
function LocalizedSubtitles({localizationData, network, project, subtitles, updateModeData}: ILocalizedSubtitles) {
    const classes = useStyles();
    const contentRef = useRef<HTMLDivElement>(null);
    const {track, untrack} = useContext(FirebaseContext);
    const [blockNames, setBlockNames] = useState<string[]>([]);
    const [blockNameFilter, setBlockNameFilter] = useState("");
    const [contentBounds, setContentBounds] = useState({height: 1000, width: 1000});
    const [edit, setEdit] = useState<{blockName: string, language: string}>();
    const renderTableRow = useCallback(({index, style}) => {
        const blockName = blockNames[index];

        return <SubtitleRow key={blockName} blockName={blockName}
                     onClick={(blockName: string, language: string) => setEdit({blockName, language})}
                     style={style} subtitles={subtitles[storeKeyName(blockName)] || {}}/>
    }, [blockNames, subtitles]);

    const handleEditLocalizedClose = () =>
        setEdit(undefined);

    useEffect(() => {
        if (localizationData.exportJson && subtitles) {
            const blob = new Blob([JSON.stringify(exportSubtitles(subtitles))], {
                type: "text/plain;charset=utf-8"
            });

            saveAs(blob, "loc_subtitles.json");
            updateModeData({exportJson: false});
        }
    }, [subtitles, localizationData, updateModeData])
    useEffect(() => {
        const blockNames: string[] = [];

        Object.values(network.states || {}).filter(state => state.meta?.blockNames).forEach(state =>
            blockNames.push(...state.meta!.blockNames!.filter(blockName => ! blockNames.includes(blockName)))
        );
        setBlockNames(blockNames
            .filter(blockName => blockName.indexOf(blockNameFilter) >= 0)
            .sort((lhs, rhs) => lhs.localeCompare(rhs)));
    }, [network, blockNameFilter]);
    useEffect(() => {
        if (contentRef.current) {
            const {height, width} = contentRef.current.getBoundingClientRect();

            setContentBounds({height, width});
        }
    }, [subtitles]);
    useEffect(() => {
        track(`subtitles/${project}`, ["subtitles"]);
        return () => untrack(`subtitles/${project}`);
    }, [project, track, untrack]);

    console.log(edit?.blockName);
    return (
        <Box className={classes.content} ref={contentRef}>
            {edit && <UpdateSubtitleDialog blockName={edit.blockName} language={edit.language}
                                           subtitles={subtitles[storeKeyName(edit.blockName)]} onClose={handleEditLocalizedClose}
                                           project={project}/>}
            <Box className={classes.table}>
                <Box className={classes.tableHead} display="flex">
                    <Box className={classes.blockName}>
                        <Typography><strong>Block Name</strong></Typography>
                        <TextField label="Filter" onChange={({target}) => setBlockNameFilter(target.value)}
                                   size="small" sx={{flexGrow: 1, marginLeft: 1}} value={blockNameFilter}/>
                    </Box>
                    <Box className={classes.date}><Typography><strong>Modified</strong></Typography></Box>
                    {Object.keys(LocalizedLanguages).map(language =>
                        <Box className={classes.language} key={language}>
                            <Typography>
                                <strong className={classes.languageLabel}>{LocalizedLanguages[language]}</strong>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
            <FixedSizeList className={classes.tableContent} itemSize={40} height={contentBounds.height}
                           itemCount={blockNames.length} width={contentBounds.width}>
                {renderTableRow}
            </FixedSizeList>
        </Box>
    );
}
const mapStateToProps = (store: never) => ({
    localizationData: selectModeData(store) as ILocalizationData,
    network: selectNetwork(store),
    subtitles: selectSubtitles(store) || {}
});

const connected = connect(mapStateToProps, {
    updateModeData
})(LocalizedSubtitles);
export {connected as LocalizedSubtitles};